import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_RMA_DETAILS } from 'api/graphql/queries/RMA/RMADetails';
import { useTranslation } from 'react-i18next';

// Enum for resolution types
export enum ResolutionType {
  REFUND = 1,
  REPLACE = 2,
  CANCEL = 3
}

// This function gets the translated labels
export const getResolutionTypeLabels = (): Record<ResolutionType, string> => {
  const { t } = useTranslation();
  return {
    [ResolutionType.REFUND]: t('refunds.resolutionTypes.refund'),
    [ResolutionType.REPLACE]: t('refunds.resolutionTypes.replace'),
    [ResolutionType.CANCEL]: t('refunds.resolutionTypes.cancel', 'Cancel items')
  };
};

// Icons for resolution types
export const resolutionTypeIcons: Record<ResolutionType, string> = {
  [ResolutionType.REFUND]: 'credit-card',
  [ResolutionType.REPLACE]: 'refresh-cw',
  [ResolutionType.CANCEL]: 'x-circle'
};

// Badge types for resolution types
export const resolutionTypeBadges: Record<ResolutionType, string> = {
  [ResolutionType.REFUND]: 'primary',
  [ResolutionType.REPLACE]: 'success',
  [ResolutionType.CANCEL]: 'danger'
};

// Format date as "MMM d, yyyy, h:mm a" - e.g., "Dec 12, 2025, 12:56 PM"
const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // Get month abbreviation
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    
    // Get day and year
    const day = date.getDate();
    const year = date.getFullYear();
    
    // Get time in 12-hour format with AM/PM
    let hours = date.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${month} ${day}, ${year}, ${hours}:${minutes} ${ampm}`;
  } catch (e) {
    console.error('Error formatting date:', e);
    return dateString; // Return original string if formatting fails
  }
};

export interface RmaAttribute {
  code: string;
  value: string;
}

export interface RmaProduct {
  product_id: number;
  product_image: string;
  product_name: string;
  product_price: number;
  product_sku: string;
  qty: number;
  reason: string;
  attributes?: RmaAttribute[];
}

export interface RmaDetailsData {
  additional_info: string;
  expedition_number: string;
  number: string; // Used for backwards compatibility
  order_id: number;
  order_number: string;
  refunded_amount: number;
  resolution_type: number;
  rma_id: number;
  status: {
    code: string; // Status code is a string from the API
  };
  creation_date?: string; // From GraphQL query
  created_at?: string;    // Alias/alternative date field
  customer_email?: string;
  customer_name?: string;
  products: RmaProduct[];
  loading: boolean;
  error: any;
}

export function useRmaDetails() {
  const { t } = useTranslation();
  // Get translated labels
  const resolutionTypeLabels = getResolutionTypeLabels();
  // Get the rma_id from URL params
  const { rmaId } = useParams<{ rmaId: string }>();
  
  // Convert rmaId to number for GraphQL query
  const rmaIdNumber = rmaId ? parseInt(rmaId, 10) : 0;
  
  // Execute the GraphQL query
  const { loading, error, data } = useQuery(GET_RMA_DETAILS, {
    variables: { rma_id: rmaIdNumber },
    skip: !rmaIdNumber || isNaN(rmaIdNumber),
    fetchPolicy: 'network-only'
  });
  
  // Transform the raw data to our expected format
  let rmaDetails: RmaDetailsData | null = null;
  
  if (data?.rmaDetails) {
    rmaDetails = {
      ...data.rmaDetails,
      // Map expedition_number to number for backward compatibility
      number: data.rmaDetails.expedition_number,
      loading,
      error
    };
  }
  
  // Helper function to get resolution type label
  const getResolutionTypeLabel = (resolutionType?: number): string => {
    if (resolutionType === undefined) return 'Unknown';
    
    return resolutionTypeLabels[resolutionType as ResolutionType] || 'Unknown';
  };
  
  // Helper function to get resolution type icon
  const getResolutionTypeIcon = (resolutionType?: number): string => {
    if (resolutionType === undefined) return 'help-circle';
    
    return resolutionTypeIcons[resolutionType as ResolutionType] || 'help-circle';
  };
  
  // Helper function to get resolution type badge color
  const getResolutionTypeBadge = (resolutionType?: number): string => {
    if (resolutionType === undefined) return 'secondary';
    
    return resolutionTypeBadges[resolutionType as ResolutionType] || 'secondary';
  };
  
  return {
    rmaDetails,
    loading,
    error,
    getResolutionTypeLabel,
    getResolutionTypeIcon,
    getResolutionTypeBadge
  };
}
